import { useEffect, useState, useCallback } from "react";

type TimeMode = "day" | "evening" | "night";

/**
 * Hook that applies subtle environmental adjustments based on time of day:
 * - Day (6 AM - 6 PM): Normal colors
 * - Evening (6 PM - 9 PM): Subtle warm background tint
 * - Night (9 PM - 6 AM): Auto dark mode (with user override capability)
 */
export function useTimeBasedWarmth() {
  const getTimeMode = (): TimeMode => {
    const hour = new Date().getHours();
    if (hour >= 21 || hour < 6) return "night";
    if (hour >= 18) return "evening";
    return "day";
  };

  const [timeMode, setTimeMode] = useState<TimeMode>(getTimeMode);
  
  // Track if user manually overrode auto-night mode this session
  const [userOverride, setUserOverride] = useState(() => 
    typeof window !== "undefined" && sessionStorage.getItem("nightModeOverride") === "true"
  );

  // Track if auto-night was applied this session (to avoid re-triggering)
  const [autoNightApplied, setAutoNightApplied] = useState(false);

  const clearOverride = useCallback(() => {
    sessionStorage.removeItem("nightModeOverride");
    setUserOverride(false);
  }, []);

  const setOverride = useCallback(() => {
    sessionStorage.setItem("nightModeOverride", "true");
    setUserOverride(true);
  }, []);

  useEffect(() => {
    const checkTime = () => {
      const newMode = getTimeMode();
      
      // Only update state when mode actually changes to avoid unnecessary renders
      setTimeMode((prev) => {
        if (prev === newMode) return prev;
        return newMode;
      });
      
      // Reset override flag when transitioning back to day
      if (newMode === "day") {
        clearOverride();
        setAutoNightApplied(false);
      }
    };

    // Check every minute for time changes
    const interval = setInterval(checkTime, 60 * 1000);
    return () => clearInterval(interval);
  }, [clearOverride]);

  useEffect(() => {
    const root = document.documentElement;
    
    // Apply evening warmth class during evening and night (unless dark mode is active)
    if (timeMode !== "day") {
      root.classList.add("evening-warmth");
    } else {
      root.classList.remove("evening-warmth");
    }

    // Auto-switch to dark mode after 9 PM if user hasn't overridden
    if (timeMode === "night" && !userOverride && !autoNightApplied) {
      const currentTheme = localStorage.getItem("theme");
      if (currentTheme !== "dark") {
        root.classList.remove("light");
        root.classList.add("dark");
        localStorage.setItem("theme", "dark");
        setAutoNightApplied(true);
      }
    }

    return () => {
      // Don't remove evening-warmth on unmount if still evening/night
    };
  }, [timeMode, userOverride, autoNightApplied]);

  return { 
    timeMode, 
    isEvening: timeMode !== "day", 
    isNight: timeMode === "night",
    userOverride, 
    setOverride,
    clearOverride 
  };
}
